import React, { useState, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Button,
  Divider,
  Chip,
  Skeleton,
  useTheme,
  alpha,
  Tooltip,
  Link,
  Stack,
} from "@mui/material";
import {
  NotificationsActive as NotificationsActiveIcon,
  MarkEmailRead as MarkEmailReadIcon,
  Circle as CircleIcon,
  Info as InfoIcon,
  DoneAll as DoneAllIcon,
  Refresh as RefreshIcon,
  InboxOutlined as InboxOutlinedIcon,
  PersonAdd as PersonAddIcon,
  GroupAdd as GroupAddIcon,
  PlayCircle as PlayCircleIcon,
  Schedule as ScheduleIcon,
  ErrorOutline as ErrorOutlineIcon,
  Celebration as CelebrationIcon,
  VerifiedUser as VerifiedUserIcon,
  HowToReg as HowToRegIcon,
  ThumbDown as ThumbDownIcon,
} from "@mui/icons-material";
import {
  useGetNotificationsQuery,
  useReadNotificationMutation,
  useReadAllNotificationsMutation,
  type Notification,
  type NotificationData,
} from "components/Slices/notificationsApiSlice";

// Notification type constants (matching backend)
const NOTIFICATION_TYPES = {
  CIRCLE_INVITE: "circle_invite",
  MEMBER_ACCEPTED: "member_accepted",
  CIRCLE_STARTED: "circle_started",
  PAYMENT_UPCOMING: "payment_upcoming",
  PAYMENT_OVERDUE: "payment_overdue",
  WELCOME: "welcome",
  ACCOUNT_VERIFIED: "account_verified",
  VERIFICATION_FAILED: "verification_failed",
  // Public circle join request notifications
  JOIN_REQUEST: "join_request",
  JOIN_APPROVED: "join_approved",
  JOIN_DECLINED: "join_declined",
} as const;

// Helper to get icon and color based on notification type
const getNotificationStyle = (type: string) => {
  switch (type) {
    // Circle Starting
    case NOTIFICATION_TYPES.CIRCLE_INVITE:
      return {
        icon: <GroupAddIcon />,
        color: "secondary.main",
        bgColor: "secondary.light",
      };
    case NOTIFICATION_TYPES.MEMBER_ACCEPTED:
      return {
        icon: <PersonAddIcon />,
        color: "success.main",
        bgColor: "success.light",
      };
    case NOTIFICATION_TYPES.CIRCLE_STARTED:
      return {
        icon: <PlayCircleIcon />,
        color: "primary.main",
        bgColor: "primary.light",
      };

    // Circle Active - Payments
    case NOTIFICATION_TYPES.PAYMENT_UPCOMING:
      return {
        icon: <ScheduleIcon />,
        color: "warning.main",
        bgColor: "warning.light",
      };
    case NOTIFICATION_TYPES.PAYMENT_OVERDUE:
      return {
        icon: <ErrorOutlineIcon />,
        color: "error.main",
        bgColor: "error.light",
      };

    // Onboarding
    case NOTIFICATION_TYPES.WELCOME:
      return {
        icon: <CelebrationIcon />,
        color: "primary.main",
        bgColor: "primary.light",
      };
    case NOTIFICATION_TYPES.ACCOUNT_VERIFIED:
      return {
        icon: <VerifiedUserIcon />,
        color: "success.main",
        bgColor: "success.light",
      };

    // Public Circle Join Request Notifications
    case NOTIFICATION_TYPES.JOIN_REQUEST:
      return {
        icon: <PersonAddIcon />,
        color: "warning.main",
        bgColor: "warning.light",
      };
    case NOTIFICATION_TYPES.JOIN_APPROVED:
      return {
        icon: <HowToRegIcon />,
        color: "success.main",
        bgColor: "success.light",
      };
    case NOTIFICATION_TYPES.JOIN_DECLINED:
      return {
        icon: <ThumbDownIcon />,
        color: "error.main",
        bgColor: "error.light",
      };

    default:
      return {
        icon: <InfoIcon />,
        color: "text.secondary",
        bgColor: "grey.200",
      };
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now.getTime() - date.getTime();
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  if (diffInHours < 1) {
    const mins = Math.floor(diffInMs / (1000 * 60));
    return mins <= 1 ? "Just now" : `${mins} minutes ago`;
  } else if (diffInHours < 24) {
    const hours = Math.floor(diffInHours);
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  } else if (diffInDays < 7) {
    const days = Math.floor(diffInDays);
    return days === 1 ? "Yesterday" : `${days} days ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }
};

const NotificationItem: React.FC<{
  notification: Notification;
  onMarkAsRead: (id: number) => void;
  isMarkingRead: boolean;
}> = ({ notification, onMarkAsRead, isMarkingRead }) => {
  const theme = useTheme();
  const notificationStyle = getNotificationStyle(notification.type);

  const isUrgent = notification.type === NOTIFICATION_TYPES.PAYMENT_OVERDUE;

  return (
    <ListItem
      sx={{
        py: 2,
        px: 3,
        bgcolor: notification.isRead
          ? "transparent"
          : isUrgent
            ? alpha(theme.palette.error.main, 0.04)
            : alpha(theme.palette.primary.main, 0.04),
        borderLeft: notification.isRead
          ? "3px solid transparent"
          : isUrgent
            ? `3px solid ${theme.palette.error.main}`
            : `3px solid ${theme.palette.primary.main}`,
        transition: "all 0.2s ease-in-out",
        "&:hover": {
          bgcolor: isUrgent
            ? alpha(theme.palette.error.main, 0.08)
            : alpha(theme.palette.primary.main, 0.08),
        },
      }}
      secondaryAction={
        !notification.isRead && (
          <Tooltip title="Mark as read">
            <IconButton
              edge="end"
              onClick={() => onMarkAsRead(notification.id)}
              disabled={isMarkingRead}
              size="small"
              aria-label="Mark as read"
              sx={{
                color: isUrgent ? "error.main" : "primary.main",
                "&:hover": {
                  bgcolor: isUrgent
                    ? alpha(theme.palette.error.main, 0.1)
                    : alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <MarkEmailReadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }
    >
      <ListItemIcon
        sx={{
          minWidth: 48,
          color: notification.isRead
            ? "text.secondary"
            : notificationStyle.color,
        }}
      >
        {notificationStyle.icon}
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: notification.isRead ? 400 : 600,
                color: notification.isRead ? "text.secondary" : "text.primary",
              }}
            >
              {notification.title}
            </Typography>
            {!notification.isRead && (
              <CircleIcon
                sx={{
                  fontSize: 8,
                  color: isUrgent ? "error.main" : "primary.main",
                }}
              />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: notification.isRead ? "text.disabled" : "text.secondary",
                mb: 0.5,
              }}
            >
              <NotificationBody
                body={notification.body}
                data={notification.data}
                type={notification.type}
              />
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: "text.disabled", fontStyle: "italic" }}
            >
              {formatDate(notification.createdAt)}
            </Typography>
          </Box>
        }
      />
    </ListItem>
  );
};

// Helper to format currency
const formatCurrency = (amount: number, currency: string = "USD") => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
};

// Helper to format due date
const formatDueDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

// Styled link component for circles
const CircleLink: React.FC<{ circleId: string; circleName: string }> = ({
  circleId,
  circleName,
}) => (
  <Link
    href={`/circles/${circleId}`}
    sx={{
      color: "primary.main",
      textDecoration: "underline",
      cursor: "pointer",
      fontWeight: 500,
      "&:hover": {
        color: "primary.dark",
      },
    }}
  >
    {circleName}
  </Link>
);

// Styled link component for actions
const ActionLink: React.FC<{ href: string; label: string }> = ({
  href,
  label,
}) => (
  <Link
    href={href}
    sx={{
      color: "primary.main",
      textDecoration: "underline",
      cursor: "pointer",
      fontWeight: 500,
      "&:hover": {
        color: "primary.dark",
      },
    }}
  >
    {label}
  </Link>
);

// Helper to render body with circle link if applicable
const renderBodyWithCircleLink = (
  body: string,
  circleName?: string,
  circleId?: string,
) => {
  if (!circleName || !circleId) {
    return <>{body}</>;
  }

  // Try to find the circle name in the body (with or without quotes)
  const patterns = [`"${circleName}"`, `'${circleName}'`, circleName];
  let matchedPattern: string | null = null;

  for (const pattern of patterns) {
    if (body.includes(pattern)) {
      matchedPattern = pattern;
      break;
    }
  }

  if (!matchedPattern) {
    return <>{body}</>;
  }

  const parts = body.split(matchedPattern);
  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {part}
          {index < parts.length - 1 && (
            <CircleLink circleId={circleId} circleName={circleName} />
          )}
        </React.Fragment>
      ))}
    </>
  );
};

const NotificationBody: React.FC<{
  body: string;
  data: NotificationData;
  type: string;
}> = ({ body, data, type }) => {
  // Runtime type checks - cast to unknown first for flexibility
  const dataAny = data as unknown as Record<string, unknown>;

  const hasCircleInfo = () =>
    typeof dataAny?.circleId === "string" &&
    typeof dataAny?.circleName === "string";

  const hasPaymentInfo = () =>
    typeof dataAny?.amount === "number" &&
    typeof dataAny?.currency === "string";

  const hasDueDate = () => typeof dataAny?.dueDate === "string";

  switch (type) {
    case NOTIFICATION_TYPES.VERIFICATION_FAILED:
      return (
        <>
          {body}{" "}
          <ActionLink href="/onboarding" label="Tap to retry verification →" />
        </>
      );
    case NOTIFICATION_TYPES.WELCOME:
      return (
        <>
          {body} <ActionLink href="/onboarding" label="Complete your setup →" />
        </>
      );

    // Onboarding - Account verified with browse link
    case NOTIFICATION_TYPES.ACCOUNT_VERIFIED:
      return (
        <>
          {body} <ActionLink href="/view-group" label="Browse circles →" />
        </>
      );

    // Circle invite - link to circle
    case NOTIFICATION_TYPES.CIRCLE_INVITE:
      if (hasCircleInfo()) {
        const d = data as { circleId: string; circleName: string };
        return renderBodyWithCircleLink(body, d.circleName, d.circleId);
      }
      return <>{body}</>;

    // Member accepted - show who joined with circle link
    case NOTIFICATION_TYPES.MEMBER_ACCEPTED:
      if (hasCircleInfo()) {
        const d = data as { circleId: string; circleName: string };
        return renderBodyWithCircleLink(body, d.circleName, d.circleId);
      }
      return <>{body}</>;

    // Circle started - link to circle
    case NOTIFICATION_TYPES.CIRCLE_STARTED:
      if (hasCircleInfo()) {
        const d = data as { circleId: string; circleName: string };
        return renderBodyWithCircleLink(body, d.circleName, d.circleId);
      }
      return <>{body}</>;

    // Payment upcoming - show amount, due date, and circle link
    case NOTIFICATION_TYPES.PAYMENT_UPCOMING:
      if (hasCircleInfo() && hasPaymentInfo() && hasDueDate()) {
        const d = data as {
          circleId: string;
          circleName: string;
          amount: number;
          currency: string;
          dueDate: string;
        };
        return (
          <>
            {renderBodyWithCircleLink(body, d.circleName, d.circleId)}
            <Box
              component="span"
              sx={{
                display: "block",
                mt: 0.5,
                fontSize: "0.8rem",
                color: "warning.dark",
                fontWeight: 500,
              }}
            >
              {formatCurrency(d.amount, d.currency)} due{" "}
              {formatDueDate(d.dueDate)}
            </Box>
          </>
        );
      }
      if (hasCircleInfo()) {
        const d = data as { circleId: string; circleName: string };
        return renderBodyWithCircleLink(body, d.circleName, d.circleId);
      }
      return <>{body}</>;

    // Payment overdue - show amount with urgency and circle link
    case NOTIFICATION_TYPES.PAYMENT_OVERDUE:
      if (hasCircleInfo() && hasPaymentInfo()) {
        const d = data as {
          circleId: string;
          circleName: string;
          amount: number;
          currency: string;
        };
        return (
          <>
            {renderBodyWithCircleLink(body, d.circleName, d.circleId)}
            <Box
              component="span"
              sx={{
                display: "block",
                mt: 0.5,
                fontSize: "0.8rem",
                color: "error.main",
                fontWeight: 600,
              }}
            >
              {formatCurrency(d.amount, d.currency)} overdue
            </Box>
          </>
        );
      }
      if (hasCircleInfo()) {
        const d = data as { circleId: string; circleName: string };
        return renderBodyWithCircleLink(body, d.circleName, d.circleId);
      }
      return <>{body}</>;

    // Join request - admin should review (navigate to circle)
    case NOTIFICATION_TYPES.JOIN_REQUEST:
      if (hasCircleInfo()) {
        const d = data as { circleId: string; circleName: string };
        return (
          <>
            {renderBodyWithCircleLink(body, d.circleName, d.circleId)}
            <Box
              component="span"
              sx={{
                display: "block",
                mt: 0.5,
                fontSize: "0.8rem",
                color: "warning.dark",
                fontWeight: 500,
              }}
            >
              Tap to review pending requests
            </Box>
          </>
        );
      }
      return <>{body}</>;

    // Join approved - user can now access the circle
    case NOTIFICATION_TYPES.JOIN_APPROVED:
      if (hasCircleInfo()) {
        const d = data as { circleId: string; circleName: string };
        return (
          <>
            {renderBodyWithCircleLink(body, d.circleName, d.circleId)}
            <Box
              component="span"
              sx={{
                display: "block",
                mt: 0.5,
                fontSize: "0.8rem",
                color: "success.dark",
                fontWeight: 500,
              }}
            >
              Tap to view your new circle
            </Box>
          </>
        );
      }
      return <>{body}</>;

    // Join declined - optionally link to discover more circles
    case NOTIFICATION_TYPES.JOIN_DECLINED:
      return (
        <>
          {body}{" "}
          <ActionLink href="/view-group" label="Discover other circles →" />
        </>
      );

    default:
      return <>{body}</>;
  }
};

const EmptyState: React.FC = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        mb: 3,
      }}
    >
      <Stack
        direction="column"
        alignItems="center"
        justifyContent="center"
        gap={1}
        height="100%"
      >
        <InboxOutlinedIcon
          sx={{ fontSize: 60, color: "primary.main", opacity: 0.7, mt: 2 }}
        />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No notifications yet
        </Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center">
          When you receive notifications, they'll appear here.
        </Typography>
      </Stack>
    </Box>
  );
};

const LoadingSkeleton: React.FC = () => (
  <List disablePadding>
    {[1, 2, 3, 4, 5].map((i) => (
      <React.Fragment key={i}>
        <ListItem sx={{ py: 2, px: 3 }}>
          <ListItemIcon>
            <Skeleton variant="circular" width={24} height={24} />
          </ListItemIcon>
          <ListItemText
            primary={<Skeleton width="60%" height={24} />}
            secondary={
              <>
                <Skeleton width="90%" height={20} />
                <Skeleton width="30%" height={16} />
              </>
            }
          />
        </ListItem>
        {i < 5 && <Divider />}
      </React.Fragment>
    ))}
  </List>
);

const NotificationsPage: React.FC = () => {
  const theme = useTheme();
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const { data, isLoading, isFetching, refetch } = useGetNotificationsQuery({
    cursor,
    limit: 10,
  });

  const [readNotification, { isLoading: isMarkingRead }] =
    useReadNotificationMutation();
  const [readAllNotifications, { isLoading: isMarkingAllRead }] =
    useReadAllNotificationsMutation();

  const handleMarkAsRead = useCallback(
    async (id: number) => {
      try {
        await readNotification({ id }).unwrap();
      } catch (error) {
        console.error("Failed to mark notification as read:", error);
      }
    },
    [readNotification],
  );

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await readAllNotifications().unwrap();
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
    }
  }, [readAllNotifications]);

  const handleLoadMore = useCallback(() => {
    if (data?.nextCursor) {
      setCursor(data.nextCursor);
    }
  }, [data?.nextCursor]);

  const notifications = data?.notifications || [];
  const hasUnread = notifications.some((n) => !n.isRead);
  const hasMore = Boolean(data?.nextCursor);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        pt: 4,
        pb: 8,
      }}
    >
      <Container maxWidth="md">
        <Box
          sx={{
            display: "flex",
            // Stack on phones: the action buttons otherwise wrap into a
            // vertical sliver next to the title at 390px.
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: { xs: 2, sm: 0 },
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                bgcolor: "primary.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 14px ${alpha(
                  theme.palette.primary.main,
                  0.4,
                )}`,
              }}
            >
              <NotificationsActiveIcon sx={{ fontSize: 28, color: "white" }} />
            </Box>
            <Box>
              <Typography variant="h4" fontWeight={700} color="text.primary">
                Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Stay updated on your circles and payments
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => refetch()}
                disabled={isFetching}
                aria-label="Refresh notifications"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <RefreshIcon
                  sx={{
                    animation: isFetching ? "spin 1s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </Tooltip>
            {hasUnread && (
              <Button
                variant="outlined"
                startIcon={<DoneAllIcon />}
                onClick={handleMarkAllAsRead}
                disabled={isMarkingAllRead}
                size="small"
                sx={{
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                Mark all as read
              </Button>
            )}
          </Box>
        </Box>
        {notifications.length > 0 && (
          <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
            <Chip
              label={`${notifications.length} Total`}
              size="small"
              variant="outlined"
            />
            <Chip
              label={`${notifications.filter((n) => !n.isRead).length} Unread`}
              size="small"
              color="primary"
              variant={hasUnread ? "filled" : "outlined"}
            />
          </Box>
        )}
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            border: `1px solid ${theme.palette.divider}`,
            overflow: "hidden",
          }}
        >
          {isLoading ? (
            <LoadingSkeleton />
          ) : notifications.length === 0 ? (
            <EmptyState />
          ) : (
            <>
              <List disablePadding>
                {notifications.map((notification, index) => (
                  <React.Fragment key={notification.id}>
                    <NotificationItem
                      notification={notification}
                      onMarkAsRead={handleMarkAsRead}
                      isMarkingRead={isMarkingRead}
                    />
                    {index < notifications.length - 1 && (
                      <Divider component="li" />
                    )}
                  </React.Fragment>
                ))}
              </List>

              {hasMore && (
                <Box
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "center",
                    borderTop: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Button
                    onClick={handleLoadMore}
                    disabled={isFetching}
                    variant="text"
                    sx={{ textTransform: "none" }}
                  >
                    {isFetching ? "Loading..." : "Load more"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default NotificationsPage;
